import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Copy,
  Maximize2,
  AlignVerticalSpaceAround,
  Pencil,
  Plus,
  Rows3,
  Settings2,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatCurrency } from '@/lib/formatters';
import { currentYear, formatMonthLabel, toMonthKey } from '@/lib/months';
import { monthRange, type WorkbookSettings } from '@/features/projections/engine';
import {
  useProjectionModel,
  type ProjectionModel,
} from '@/features/projections/useProjectionModel';
import {
  BASE_SCENARIO,
  useCloneScenario,
  useDeleteScenario,
  useEnsureBaseWorkbook,
  useUpdateWorkbook,
  useWorkbooks,
} from '@/features/projections/workbookHooks';
import { CapitalPlanningTab } from './CapitalPlanningTab';
import { EmployeePayrollTab } from './EmployeePayrollTab';
import { ArApTab } from './ArApTab';
import { SheetTab, type FocusRequest } from './SheetTab';
import { SalesDriversTab } from './SalesDriversTab';
import { InsightsTab } from './InsightsTab';
import type { ColumnMode } from './ProjectionsGrid';
import { DEFAULT_DENSITY, ROW_HEIGHTS, type RowDensity } from '@/features/projections/density';

/**
 * Main Financials workspace (route: /financials/projections).
 *
 * One calculation engine, several views:
 *   Main          — the operator's hybrid sheet: sales drivers → P&L → cash
 *                   movements → month-end bank balance, with checks.
 *   Sales Drivers — customers, models, prices, mix; units by month.
 *   P&L / Balance sheet / Cash flow — classic statements over the same model.
 *   Capital Planning / Employee Payroll — detail tabs that feed the model.
 *   AR & AP       — receivables / payables aging straight from QuickBooks.
 *   Insights      — automatic flags, integrity checks, Claude analysis.
 *
 * Scenario, range, zoom and column mode live here so switching tabs never
 * loses them; the active tab is mirrored to `?tab=` for deep links.
 */
const TABS = [
  { id: 'main', label: 'Main' },
  { id: 'sales', label: 'Sales Drivers' },
  { id: 'pnl', label: 'P&L' },
  { id: 'balance-sheet', label: 'Balance sheet' },
  { id: 'cashflow', label: 'Cash flow' },
  { id: 'capital', label: 'Capital Planning' },
  { id: 'payroll', label: 'Employee Payroll' },
  { id: 'ar-ap', label: 'AR & AP' },
  { id: 'insights', label: 'Insights & checks' },
] as const;

type TabId = (typeof TABS)[number]['id'];
const TAB_IDS = TABS.map((t) => t.id);

interface Prefs {
  zoom: number;
  columnMode: ColumnMode;
  density: RowDensity;
  from: string;
  to: string;
  scenario: string;
}

const PREFS_KEY = 'atlas:projections:prefs:v1';

function defaultPrefs(): Prefs {
  const y = currentYear();
  return {
    zoom: 1,
    columnMode: 'stacked',
    density: DEFAULT_DENSITY,
    from: `${y}-01-01`,
    to: `${y + 1}-12-01`,
    scenario: BASE_SCENARIO,
  };
}

function loadPrefs(): Prefs {
  const d = defaultPrefs();
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return d;
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      zoom: typeof p.zoom === 'number' && p.zoom >= 0.5 && p.zoom <= 2 ? p.zoom : d.zoom,
      columnMode:
        p.columnMode === 'split' || p.columnMode === 'stacked' ? p.columnMode : d.columnMode,
      density: p.density && p.density in ROW_HEIGHTS ? p.density : d.density,
      from: typeof p.from === 'string' && /^\d{4}-\d{2}-01$/.test(p.from) ? p.from : d.from,
      to: typeof p.to === 'string' && /^\d{4}-\d{2}-01$/.test(p.to) ? p.to : d.to,
      scenario: typeof p.scenario === 'string' && p.scenario ? p.scenario : d.scenario,
    };
  } catch {
    return d;
  }
}

const ZOOM_STEPS = [0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.25, 1.4, 1.6];

export function ProjectionsPage() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as TabId | null;
  const active: TabId = tabParam && TAB_IDS.includes(tabParam) ? tabParam : 'main';

  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  useEffect(() => {
    try {
      window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // storage unavailable — fine
    }
  }, [prefs]);
  const update = (patch: Partial<Prefs>) => setPrefs((p) => ({ ...p, ...patch }));

  const workbooks = useWorkbooks();
  const ensureBase = useEnsureBaseWorkbook();
  useEffect(() => {
    if (
      workbooks.isSuccess &&
      !workbooks.data.some((w) => w.scenario === BASE_SCENARIO) &&
      !ensureBase.isPending
    ) {
      ensureBase.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbooks.isSuccess, workbooks.data]);

  // Fall back to base if the saved scenario no longer exists.
  useEffect(() => {
    if (
      workbooks.isSuccess &&
      workbooks.data.length > 0 &&
      !workbooks.data.some((w) => w.scenario === prefs.scenario)
    ) {
      setPrefs((p) => ({ ...p, scenario: BASE_SCENARIO }));
    }
  }, [workbooks.isSuccess, workbooks.data, prefs.scenario]);

  const visibleMonths = useMemo(() => {
    const from = prefs.from <= prefs.to ? prefs.from : prefs.to;
    const to = prefs.from <= prefs.to ? prefs.to : prefs.from;
    const r = monthRange(from, to);
    return r.length > 60 ? r.slice(0, 60) : r;
  }, [prefs.from, prefs.to]);

  const pm = useProjectionModel(prefs.scenario, visibleMonths);
  const workbook = workbooks.data?.find((w) => w.scenario === prefs.scenario) ?? null;

  const [focus, setFocus] = useState<FocusRequest | null>(null);
  const jumpToMain = useCallback(
    (rowKey: string | null | undefined, month: string | null | undefined) => {
      const next = new URLSearchParams(params);
      next.set('tab', 'main');
      setParams(next, { replace: true });
      setFocus({ rowKey: rowKey ?? null, month: month ?? null, nonce: Date.now() });
    },
    [params, setParams],
  );

  // Grid height: fill the viewport below the toolbar, with a sane floor.
  const [gridHeight, setGridHeight] = useState(640);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const measure = () => {
      const top = anchorRef.current?.getBoundingClientRect().top ?? 320;
      setGridHeight(Math.max(420, Math.floor(window.innerHeight - top - 96)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const fitZoom = () => {
    const width = containerRef.current?.clientWidth ?? 1400;
    const n = visibleMonths.length;
    const needed =
      prefs.columnMode === 'stacked' ? 250 + 104 * n + 4 * 108 : 250 + 92 * 2 * n + 4 * 108;
    const z = Math.min(1.6, Math.max(0.6, Math.floor(((width - 20) / needed) * 100) / 100));
    update({ zoom: z });
  };
  const stepZoom = (dir: 1 | -1) => {
    const i = ZOOM_STEPS.findIndex((z) => z >= prefs.zoom - 0.001);
    const cur = i < 0 ? ZOOM_STEPS.length - 1 : i;
    const next = ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, cur + dir))];
    update({ zoom: next });
  };

  const sheetProps = {
    pm,
    zoom: prefs.zoom,
    columnMode: prefs.columnMode,
    density: prefs.density,
    height: gridHeight,
  };

  return (
    <div className="space-y-4" ref={containerRef}>
      <PageHeader
        title="Main Financials"
        meta={
          workbook ? (
            <span className="text-xs text-muted-foreground">
              {workbook.name}
              {pm.latestActualMonth
                ? ` · QuickBooks actuals through ${formatMonthLabel(pm.latestActualMonth)}`
                : ' · no QuickBooks actuals synced yet'}
              {pm.isSaving ? ' · saving…' : ''}
            </span>
          ) : null
        }
        actions={
          <ScenarioBar
            scenario={prefs.scenario}
            onScenario={(s) => update({ scenario: s })}
            workbooks={workbooks.data ?? []}
            settings={pm.settings}
            workbookId={pm.workbookId}
          />
        }
      />

      <KpiStrip pm={pm} />

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <RangePicker
            from={prefs.from}
            to={prefs.to}
            onChange={(from, to) => update({ from, to })}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={prefs.columnMode}
            onValueChange={(v) => update({ columnMode: v as ColumnMode })}
          >
            <SelectTrigger className="h-8 w-52 text-xs">
              <Rows3 className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="stacked">Plan over actual (one column)</SelectItem>
              <SelectItem value="split">Plan | Actual (two columns)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={prefs.density} onValueChange={(v) => update({ density: v as RowDensity })}>
            <SelectTrigger className="h-8 w-36 text-xs" aria-label="Row height">
              <AlignVerticalSpaceAround className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(ROW_HEIGHTS) as RowDensity[]).map((d) => (
                <SelectItem key={d} value={d}>
                  {ROW_HEIGHTS[d].label} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center rounded-md border border-border">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground"
              onClick={() => stepZoom(-1)}
              aria-label="Zoom out"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <input
              type="range"
              min={0.6}
              max={1.6}
              step={0.05}
              value={prefs.zoom}
              onChange={(e) => update({ zoom: Number(e.target.value) })}
              className="h-8 w-28 accent-primary"
              aria-label="Zoom"
            />
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground"
              onClick={() => stepZoom(1)}
              aria-label="Zoom in"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <span className="w-11 text-center text-xs tabular-nums text-muted-foreground">
              {Math.round(prefs.zoom * 100)}%
            </span>
            <button
              type="button"
              className="flex h-8 items-center gap-1 border-l border-border px-2 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
              onClick={fitZoom}
              title="Fit every visible month on screen"
            >
              <Maximize2 className="h-3.5 w-3.5" /> Fit
            </button>
          </div>
        </div>
      </div>

      <Tabs
        value={active}
        onValueChange={(v) => {
          const next = new URLSearchParams(params);
          next.set('tab', v);
          setParams(next, { replace: true });
        }}
      >
        <TabsList className="h-auto flex-wrap gap-1 bg-secondary p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:shadow-sm">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div ref={anchorRef} />

        <TabsContent value="main" className="mt-4">
          <SheetTab sheet="main" {...sheetProps} focusRequest={focus} />
        </TabsContent>
        <TabsContent value="sales" className="mt-4">
          <SalesDriversTab {...sheetProps} height={Math.max(360, gridHeight - 260)} />
        </TabsContent>
        <TabsContent value="pnl" className="mt-4">
          <SheetTab sheet="pnl" {...sheetProps} />
        </TabsContent>
        <TabsContent value="balance-sheet" className="mt-4">
          <SheetTab sheet="balance-sheet" {...sheetProps} />
        </TabsContent>
        <TabsContent value="cashflow" className="mt-4">
          <SheetTab sheet="cashflow" {...sheetProps} />
        </TabsContent>
        <TabsContent value="capital" className="mt-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Capital items are commitments shared by every scenario. They appear in the Main sheet
            under Investing as “Capital plan items”.
          </p>
          <CapitalPlanningTab />
        </TabsContent>
        <TabsContent value="payroll" className="mt-4">
          <EmployeePayrollTab />
        </TabsContent>
        <TabsContent value="ar-ap" className="mt-4">
          <ArApTab />
        </TabsContent>
        <TabsContent value="insights" className="mt-4">
          <InsightsTab
            pm={pm}
            scenarioName={workbook?.name ?? prefs.scenario}
            onJump={jumpToMain}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI strip
// ---------------------------------------------------------------------------

function KpiStrip({ pm }: { pm: ProjectionModel }) {
  const model = pm.model;
  if (pm.isLoading || !model) return <Skeleton className="h-20 w-full" />;
  const idx = pm.visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);
  const openIdx = idx.filter((i) => !model.monthIsClosed[i]);
  const sum = (arr: (number | null)[], ids: number[]) => ids.reduce((s, i) => s + (arr[i] ?? 0), 0);
  const forecast = (s: { plan: number[]; actual: (number | null)[] }) =>
    idx.reduce(
      (t, i) =>
        t + (model.monthIsClosed[i] && s.actual[i] !== null ? (s.actual[i] as number) : s.plan[i]),
      0,
    );

  // Latest real bank balance.
  let lastBankIdx = -1;
  for (let i = model.months.length - 1; i >= 0; i--) {
    if (model.bankBalance[i] !== null) {
      lastBankIdx = i;
      break;
    }
  }
  let minCash = Infinity;
  let minIdx = -1;
  for (const i of openIdx) {
    if (model.endingCash.plan[i] < minCash) {
      minCash = model.endingCash.plan[i];
      minIdx = i;
    }
  }
  const revF = forecast(model.revenue);
  const revPlan = sum(model.revenue.plan, idx);
  const gpF = forecast(model.grossProfit);
  const niF = forecast(model.netIncome);
  const niPlan = sum(model.netIncome.plan, idx);
  const units = sum(model.sales.totalUnits.plan, openIdx);
  const cashTone =
    minIdx < 0
      ? ''
      : minCash < 0
        ? 'text-red-700'
        : minCash < pm.settings.min_cash_cents
          ? 'text-amber-700'
          : 'text-emerald-700';

  const cards: Array<{ label: string; value: string; sub?: string; tone?: string }> = [
    {
      label: 'Bank balance (QuickBooks)',
      value:
        lastBankIdx >= 0
          ? formatCurrency(model.bankBalance[lastBankIdx] ?? 0, { whole: true })
          : '—',
      sub:
        lastBankIdx >= 0
          ? `as of ${formatMonthLabel(model.months[lastBankIdx])} month end`
          : 'no balance sync yet',
    },
    {
      label: 'Lowest projected cash',
      value: minIdx >= 0 ? formatCurrency(minCash, { whole: true }) : '—',
      sub:
        minIdx >= 0
          ? `${formatMonthLabel(model.months[minIdx])} · floor ${formatCurrency(pm.settings.min_cash_cents, { whole: true })}`
          : undefined,
      tone: cashTone,
    },
    {
      label: 'Revenue forecast',
      value: formatCurrency(revF, { whole: true }),
      sub: `plan ${formatCurrency(revPlan, { whole: true })} · ${delta(revF, revPlan)}`,
    },
    {
      label: 'Gross margin',
      value: revF ? `${((gpF / revF) * 100).toFixed(1)}%` : '—',
      sub: `floor ${(pm.settings.gross_margin_floor_pct * 100).toFixed(0)}%`,
      tone: revF && gpF / revF < pm.settings.gross_margin_floor_pct ? 'text-amber-700' : '',
    },
    {
      label: 'Net income forecast',
      value: formatCurrency(niF, { whole: true }),
      sub: `plan ${formatCurrency(niPlan, { whole: true })} · ${delta(niF, niPlan)}`,
      tone: niF < 0 ? 'text-red-700' : '',
    },
    {
      label: 'Units planned (open months)',
      value: units.toLocaleString('en-US'),
      sub: `${openIdx.length} month${openIdx.length === 1 ? '' : 's'}`,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg border border-border bg-card px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.label}</p>
          <p className={`mt-0.5 text-lg font-semibold tabular-nums ${c.tone ?? ''}`}>{c.value}</p>
          {c.sub && <p className="text-[11px] text-muted-foreground">{c.sub}</p>}
        </div>
      ))}
    </div>
  );
}

function delta(a: number, b: number): string {
  const d = a - b;
  if (d === 0) return 'on plan';
  const sign = d > 0 ? '+' : '−';
  return `${sign}${formatCurrency(Math.abs(d), { whole: true })} vs plan`;
}

// ---------------------------------------------------------------------------
// Range picker
// ---------------------------------------------------------------------------

function RangePicker({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const y = currentYear();
  const options = useMemo(() => monthRange(`${y - 2}-01-01`, `${y + 4}-12-01`), [y]);
  const now = toMonthKey(new Date());
  const presets: Array<{ id: string; label: string; from: string; to: string }> = [
    { id: 'y0', label: `${y}`, from: `${y}-01-01`, to: `${y}-12-01` },
    { id: 'y01', label: `${y} + ${y + 1}`, from: `${y}-01-01`, to: `${y + 1}-12-01` },
    { id: 'y1', label: `${y + 1}`, from: `${y + 1}-01-01`, to: `${y + 1}-12-01` },
    { id: 'n12', label: 'Next 12 months', from: now, to: monthRange(now, `${y + 5}-12-01`)[11] },
    { id: 'n24', label: 'Next 24 months', from: now, to: monthRange(now, `${y + 5}-12-01`)[23] },
    { id: 'n36', label: 'Next 36 months', from: now, to: monthRange(now, `${y + 5}-12-01`)[35] },
  ];
  const activePreset = presets.find((p) => p.from === from && p.to === to)?.id ?? 'custom';
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Select
        value={activePreset}
        onValueChange={(v) => {
          const p = presets.find((x) => x.id === v);
          if (p) onChange(p.from, p.to);
        }}
      >
        <SelectTrigger className="h-8 w-40 text-xs">
          <SelectValue placeholder="Range" />
        </SelectTrigger>
        <SelectContent>
          {presets.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.label}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value="custom" disabled>
            Custom
          </SelectItem>
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">from</span>
      <Select value={from} onValueChange={(v) => onChange(v, to)}>
        <SelectTrigger className="h-8 w-28 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((m) => (
            <SelectItem key={m} value={m}>
              {formatMonthLabel(m)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">to</span>
      <Select value={to} onValueChange={(v) => onChange(from, v)}>
        <SelectTrigger className="h-8 w-28 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((m) => (
            <SelectItem key={m} value={m}>
              {formatMonthLabel(m)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">
        {monthRange(from <= to ? from : to, from <= to ? to : from).length} months
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scenario bar + settings
// ---------------------------------------------------------------------------

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function ScenarioBar({
  scenario,
  onScenario,
  workbooks,
  settings,
  workbookId,
}: {
  scenario: string;
  onScenario: (s: string) => void;
  workbooks: Array<{ id: string; scenario: string; name: string }>;
  settings: WorkbookSettings;
  workbookId: string | null;
}) {
  const clone = useCloneScenario();
  const del = useDeleteScenario();
  const updateWb = useUpdateWorkbook();
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const current = workbooks.find((w) => w.scenario === scenario);

  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : 'Something went wrong');

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={scenario} onValueChange={onScenario}>
        <SelectTrigger className="h-9 w-56 text-sm">
          <SelectValue placeholder="Scenario" />
        </SelectTrigger>
        <SelectContent>
          {workbooks.map((w) => (
            <SelectItem key={w.id} value={w.scenario}>
              {w.name}
            </SelectItem>
          ))}
          {workbooks.length === 0 && <SelectItem value={BASE_SCENARIO}>Base plan</SelectItem>}
        </SelectContent>
      </Select>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            Scenario
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            onSelect={() => {
              setCloneName(`${current?.name ?? 'Plan'} — copy`);
              setCloneOpen(true);
            }}
          >
            <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate as new scenario
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              setRenameValue(current?.name ?? '');
              setRenameOpen(true);
            }}
            disabled={!current}
          >
            <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setSettingsOpen(true)} disabled={!workbookId}>
            <Settings2 className="mr-2 h-3.5 w-3.5" /> Thresholds &amp; settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={scenario === BASE_SCENARIO}
            className="text-destructive focus:text-destructive"
            onSelect={() => {
              if (!current) return;
              if (!window.confirm(`Delete scenario “${current.name}” and everything in it?`))
                return;
              del.mutate(scenario, {
                onError,
                onSuccess: () => {
                  toast.success('Scenario deleted');
                  onScenario(BASE_SCENARIO);
                },
              });
            }}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete scenario
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New scenario</DialogTitle>
            <DialogDescription>
              Copies every assumption in “{current?.name ?? scenario}” — sales units, plans, custom
              lines, highlights — into a scenario you can change freely. QuickBooks actuals are
              shared.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              const name = cloneName.trim();
              if (!name) return;
              let slug = slugify(name) || `scenario-${Date.now()}`;
              if (workbooks.some((w) => w.scenario === slug))
                slug = `${slug}-${Date.now().toString(36)}`;
              clone.mutate(
                { from: scenario, to: slug, name },
                {
                  onError,
                  onSuccess: () => {
                    toast.success(`Scenario “${name}” created`);
                    setCloneOpen(false);
                    onScenario(slug);
                  },
                },
              );
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="scenario-name">Name</Label>
              <Input
                id="scenario-name"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder="e.g. Downside — Costco slips to Q3"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCloneOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={clone.isPending}>
                <Plus className="h-3.5 w-3.5" /> Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename scenario</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!current || !renameValue.trim()) return;
              updateWb.mutate(
                { id: current.id, name: renameValue.trim() },
                { onError, onSuccess: () => setRenameOpen(false) },
              );
            }}
          >
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {workbookId && (
        <SettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          settings={settings}
          onSave={(next) =>
            updateWb.mutate(
              { id: workbookId, settings: next as unknown as Record<string, number | null> },
              { onError, onSuccess: () => toast.success('Settings saved') },
            )
          }
        />
      )}
    </div>
  );
}

function SettingsDialog({
  open,
  onOpenChange,
  settings,
  onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  settings: WorkbookSettings;
  onSave: (s: WorkbookSettings) => void;
}) {
  const [draft, setDraft] = useState<WorkbookSettings>(settings);
  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);

  const num = (v: string) => {
    const n = Number(v.replace(/[$,%\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Thresholds &amp; settings</DialogTitle>
          <DialogDescription>
            Drive the conditional formatting, the automatic flags and the AI analysis for this
            scenario.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave(draft);
            onOpenChange(false);
          }}
        >
          <Field
            label="Minimum cash floor ($)"
            hint="Ending bank balance below this turns amber; negative turns red."
          >
            <Input
              value={(draft.min_cash_cents / 100).toString()}
              onChange={(e) =>
                setDraft({ ...draft, min_cash_cents: Math.round(num(e.target.value) * 100) })
              }
              inputMode="decimal"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Variance threshold (%)"
              hint="Actual vs plan must differ by at least this…"
            >
              <Input
                value={(draft.variance_pct_threshold * 100).toString()}
                onChange={(e) =>
                  setDraft({ ...draft, variance_pct_threshold: num(e.target.value) / 100 })
                }
                inputMode="decimal"
              />
            </Field>
            <Field label="…and by at least ($)" hint="to be flagged.">
              <Input
                value={(draft.variance_abs_cents / 100).toString()}
                onChange={(e) =>
                  setDraft({ ...draft, variance_abs_cents: Math.round(num(e.target.value) * 100) })
                }
                inputMode="decimal"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gross margin floor (%)">
              <Input
                value={(draft.gross_margin_floor_pct * 100).toString()}
                onChange={(e) =>
                  setDraft({ ...draft, gross_margin_floor_pct: num(e.target.value) / 100 })
                }
                inputMode="decimal"
              />
            </Field>
            <Field label="Fiscal year starts (month #)">
              <Input
                value={draft.fiscal_year_start_month.toString()}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    fiscal_year_start_month: Math.min(
                      12,
                      Math.max(1, Math.round(num(e.target.value)) || 1),
                    ),
                  })
                }
                inputMode="numeric"
              />
            </Field>
          </div>
          <Field
            label="Opening cash override ($)"
            hint="Only used when QuickBooks has no bank balance before the first month shown. Leave blank to use QuickBooks."
          >
            <Input
              value={
                draft.opening_cash_cents === null ? '' : (draft.opening_cash_cents / 100).toString()
              }
              onChange={(e) =>
                setDraft({
                  ...draft,
                  opening_cash_cents:
                    e.target.value.trim() === '' ? null : Math.round(num(e.target.value) * 100),
                })
              }
              placeholder="Use QuickBooks"
              inputMode="decimal"
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
